import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "wouter";
import { ArrowRight, Settings, Wrench } from "lucide-react";
import CompanyLogos from "@/components/CompanyLogos";

export default function Home() {
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section 
        className="relative flex min-h-[700px] items-center justify-center overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)"
        }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div 
            className="w-full h-full"
            style={{
              backgroundImage: "url('/images/ring-frame-machine-hq.png')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "contrast(1.2) brightness(0.3)"
            }}
          ></div>
        </div>
        
        {/* Floating geometric elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-br from-orange-400/20 to-orange-600/20 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-32 right-16 w-32 h-32 bg-gradient-to-br from-blue-400/20 to-blue-600/20 rounded-full blur-xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 right-1/4 w-16 h-16 bg-gradient-to-br from-orange-400/30 to-orange-600/30 rotate-45 blur-lg animate-bounce"></div>

        <div className="container relative z-10 mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8">
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-orange-500/20 to-orange-600/20 text-orange-300 text-sm font-semibold rounded-full border border-orange-400/30 backdrop-blur-sm">
                20+ Years of Excellence
              </span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold leading-tight text-white mb-6">
              <span className="bg-gradient-to-r from-white via-slate-100 to-orange-200 bg-clip-text text-transparent">
                Precision Engineering
              </span>
              <br />
              <span className="text-orange-400">
                for Textile Industry
              </span>
            </h1>
            
            <p className="mt-6 text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Manufacturing high-quality machinery components and delivering expert repair services that power the world's textile production
            </p>
            
            <div className="mt-12 flex flex-col sm:flex-row gap-6 justify-center">
              <Button asChild size="lg" className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-2xl hover:shadow-orange-500/25 transform hover:scale-105 transition-all duration-300 px-8 py-4 text-lg font-semibold">
                <Link href="/products">
                  Explore Products <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>

              <Button asChild size="lg" variant="outline" className="border-2 border-white font-semibold text-sky-900 hover:bg-orange-300 hover:text-slate-900 backdrop-blur-sm px-8 py-4 text-lg font-semibold transition-all duration-300">
                <Link href="/contact">
                  Get Quote
                </Link>
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="mt-16 grid grid-cols-3 gap-8 text-center">
              <div className="space-y-2">
                <div className="text-3xl font-bold text-orange-400">20+</div>
                <div className="text-slate-300 text-sm">Years Experience</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-orange-400">500+</div>
                <div className="text-slate-300 text-sm">Projects Completed</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-orange-400">100%</div>
                <div className="text-slate-300 text-sm">Client Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gradient-to-br from-slate-50 to-white relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-orange-100/30 to-orange-200/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-blue-100/30 to-slate-100/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="container px-4 md:px-6 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-gradient-to-r from-orange-100 to-orange-50 text-orange-600 text-sm font-semibold rounded-full border border-orange-200 mb-6">
              Our Expertise
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
              Why Choose Panday Engineering Works
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Experience excellence in textile machinery manufacturing with our comprehensive solutions and decades of industry expertise
            </p>
          </div>
          
          <div className="grid gap-8 md:grid-cols-3 max-w-7xl mx-auto">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Settings className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Precision Parts</h3>
                <p className="text-muted-foreground leading-relaxed">
                  High-quality machinery components manufactured to exact specifications with advanced engineering
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Wrench className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Expert Service</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Professional installation and maintenance support from our experienced technical team
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <ArrowRight className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Fast Delivery</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Quick turnaround times ensuring your production schedules remain uninterrupted
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Company Logos Section */}
      <CompanyLogos />

      {/* CTA Section */}
      <section className="py-16" style={{ backgroundColor: '#f0ede8' }}>
        <div className="container text-center">
          <h2 className="text-3xl font-bold text-slate-900">Ready to Upgrade Your Machinery?</h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-600">
            Contact us today to discuss your textile machinery needs and discover how we can help optimize your production
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <Button asChild className="bg-primary text-white hover:bg-primary/90">
              <Link href="/contact">
                Get Started <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button 
              asChild 
              className="bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-900 font-bold hover:from-amber-400 hover:to-yellow-400 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              <Link href="/contact">
                Get Quote Now
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}